import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ArrowsClockwise, CaretDown, Check, PencilSimple, Trash, X} from '../../icons';
import {Spinner} from '../../Spinner';
import {canReconnectCredential} from '../connections/reconnectCredential';
import {AccessAccounts} from './AccessAccounts';
import {ActionsMenu} from './ActionsMenu';
import {STATUS_DISCONNECTED, accountsPathFor, attentionKindOf, expiryStateOf, hasAccountList} from './platformsModel';
import {MERCADO_ADS_CHANNEL} from '../../channels/channels';
import {
    accessLabel, accessWarningText, connectedByText, mercadoAdsDisconnectedText, platformUserText, unitCountText,
} from './platformsText';

const NS = 'agency_v2.platforms';

/**
 * Un acceso de la agencia (una credencial): su nombre, que se edita ahí mismo; quién y cuándo lo
 * conectó; cuántas cuentas trae y su estado. Las acciones van en el ⋮: Renombrar · Reconectar · Quitar
 * acceso.
 *
 * En los canales de pauta (`hasAccountList`) el «N cuentas» despliega CUÁLES trae y el «¿Falta una
 * cuenta?» (`AccessAccounts`, cc#205); en el resto sigue siendo un link a la pestaña Cuentas.
 * `initiallyExpanded`: llega desplegado (el acceso que se acaba de conectar).
 *
 * No escribe nada por su cuenta: avisa al shell, que es el que reconecta, confirma y llama al hook.
 *
 * Un acceso de Mercado Ads cortado lo dice con el apodo del vendedor. Si lo conectó el cliente (Portal o
 * link), la agencia no lo puede reconectar con su OAuth: lo que ofrece es mandarle un link nuevo
 * (`onSendLink`).
 */
export function AccessRow({channel, credential, onRename, onReconnect, onRemove, onSendLink = null,
                              initiallyExpanded = false}) {
    const {t, i18n} = useTranslation();
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [reconnecting, setReconnecting] = useState(false);
    const listable = hasAccountList(channel);
    const [expanded, setExpanded] = useState(listable && initiallyExpanded);
    const accountsId = `apl-accounts-${credential.id}`;

    const language = i18n && i18n.language;
    const attention = attentionKindOf(channel, credential);
    const expiry = expiryStateOf(channel, credential);
    const reconnectable = canReconnectCredential(channel, credential);
    // De quién son las cuentas que trae (cc#209): el nombre del acceso lo escribe la agencia y no lo dice.
    const platformUser = platformUserText(t, channel, credential);
    const meta = connectedByText(t, credential, language);
    const warning = mercadoAdsDisconnectedText(t, channel, credential) || accessWarningText(t, attention, credential, language);
    const sendNewLink = channel === MERCADO_ADS_CHANNEL && credential.status === STATUS_DISCONNECTED
        && credential.managed_by_client && onSendLink;
    const count = Number(credential.ad_accounts_count) || 0;

    const startEdit = () => {
        setValue(credential.label || '');
        setEditing(true);
    };
    const cancelEdit = () => {
        setEditing(false);
        setSaving(false);
    };
    const save = (event) => {
        if (event) event.preventDefault();
        if (saving) return;
        setSaving(true);
        onRename(value.trim()).then((ok) => {
            setSaving(false);
            if (ok) setEditing(false);
        });
    };
    const reconnect = () => {
        if (reconnecting) return;
        setReconnecting(true);
        // Si se va de página, el botón queda ocupado hasta que llega; si no pudo, se libera.
        Promise.resolve(onReconnect()).then((result) => {
            if (!result || !result.ok || result.stays) setReconnecting(false);
        });
    };

    const items = [
        {key: 'rename', label: t(`${NS}.access.rename`), icon: <PencilSimple size={16} aria-hidden="true"/>, onSelect: startEdit},
    ];
    if (reconnectable) {
        items.push({key: 'reconnect', label: t(`${NS}.reconnect`), icon: <ArrowsClockwise size={16} aria-hidden="true"/>, onSelect: reconnect});
    }
    items.push({key: 'divider', divider: true});
    items.push({key: 'remove', label: t(`${NS}.access.remove`), icon: <Trash size={16} aria-hidden="true"/>, onSelect: onRemove, danger: true});

    return (
        <div className={`apl-access${attention && attention !== 'expiring' ? ' -err' : ''}${attention === 'expiring' ? ' -warn' : ''}`}
             data-testid="apl-access">
            <div className="apl-access__main">
                {editing ? (
                    <form className="apl-rename" onSubmit={save}>
                        <input className="apl-rename__input" type="text" value={value} maxLength={255} autoFocus
                               disabled={saving} placeholder={t('credential_row.edit_placeholder')}
                               aria-label={t(`${NS}.access.rename`)}
                               onChange={(event) => setValue(event.target.value)}
                               onKeyDown={(event) => {
                                   if (event.key === 'Escape') {
                                       event.preventDefault();
                                       cancelEdit();
                                   }
                               }}/>
                        <button type="submit" className="apl-icon-btn" disabled={saving} aria-label={t('common.save')}>
                            {saving ? <Spinner color="negative very-small"/> : <Check size={16} weight="bold" aria-hidden="true"/>}
                        </button>
                        <button type="button" className="apl-icon-btn" disabled={saving} aria-label={t('common.cancel')}
                                onClick={cancelEdit}>
                            <X size={16} aria-hidden="true"/>
                        </button>
                    </form>
                ) : (
                    <span className="apl-access__name">
                        <span className="apl-access__label">{accessLabel(t, credential, channel)}</span>
                        <button type="button" className="apl-icon-btn -quiet" aria-label={t(`${NS}.access.rename`)}
                                onClick={startEdit}>
                            <PencilSimple size={15} aria-hidden="true"/>
                        </button>
                        {credential.managed_by_client && (
                            <span className="apl-chip -violet">
                                {credential.managed_by_client_name
                                    ? t('managed_by_client.label_named', {name: credential.managed_by_client_name, interpolation: {escapeValue: false}})
                                    : t('managed_by_client.label')}
                            </span>
                        )}
                    </span>
                )}
                {platformUser && <span className="apl-meta apl-access__platform-user">{platformUser}</span>}
                {meta && <span className="apl-meta">{meta}</span>}
                {expiry === 'auto_renews' && <span className="apl-meta">{t('credential_row.auto_renews')}</span>}
                {warning && <span className={`apl-warning${attention === 'expiring' ? ' -soft' : ''}`}>{warning}</span>}
            </div>

            <div className="apl-access__count">
                {credential.is_syncing ? (
                    <span className="apl-syncing">
                        <Spinner color="positive small"/>
                        {t('credential_row.syncing')}
                    </span>
                ) : listable ? (
                    <button type="button" className="apl-link apl-access__toggle" aria-expanded={expanded}
                            aria-controls={accountsId} onClick={() => setExpanded((open) => !open)}>
                        {count > 0 ? unitCountText(t, channel, count) : t(`${NS}.access.no_accounts`)}
                        <CaretDown className="apl-access__caret" size={13} aria-hidden="true"/>
                    </button>
                ) : count > 0 ? (
                    <Link className="apl-link" to={accountsPathFor(channel)}>{unitCountText(t, channel, count)}</Link>
                ) : (
                    <span className="apl-meta">{t(`${NS}.access.no_accounts`)}</span>
                )}
            </div>

            <div className="apl-access__state">
                {sendNewLink ? (
                    <button type="button" className="apl-btn -sm" onClick={() => onSendLink(credential)}
                            data-testid="apl-access-send-link">
                        {t('connections.mercado_ads.send_new_link')}
                    </button>
                ) : attention ? (
                    <button type="button" className="apl-btn -sm" onClick={reconnect} disabled={reconnecting}
                            aria-busy={reconnecting || undefined}>
                        <ArrowsClockwise size={15} aria-hidden="true"/>
                        {t(`${NS}.reconnect`)}
                    </button>
                ) : credential.status === STATUS_DISCONNECTED ? (
                    // Caído y sin «Reconectar»: un Escalafy que conectó el cliente desde su portal lo
                    // reconecta él. Decir «Al día» acá sería mentir.
                    <span className="apl-chip -err">{t('credential_row.disconnected_chip')}</span>
                ) : (
                    <span className="apl-chip -ok">{t(`${NS}.access.up_to_date`)}</span>
                )}
            </div>

            <ActionsMenu label={t(`${NS}.access.actions`)} items={items}/>

            {expanded && (
                <div className="apl-access__accounts" id={accountsId}>
                    <AccessAccounts channel={channel} credential={credential} reconnectable={reconnectable}
                                    onReconnect={reconnect} reconnecting={reconnecting}/>
                </div>
            )}
        </div>
    );
}

export default AccessRow;
